/**
 * ClauseChatThread — Plan 143
 *
 * Per-clause Analysis chat in the right panel. Uses useScopedChatSession
 * (scope='clause') so the thread is durably bound to the clause on the server
 * and hydrates from getChatMessages on mount.
 *
 * Differs from IssueChatThread:
 * - No redraft UI in-thread (redraft is a separate right-panel view pushed by
 *   the parent, not posted as a chat message). So clause messages are plain
 *   user/assistant turns and rehydrate cleanly from chat_messages.
 * - Bracketed clause context is sent as `context_injection` — never written
 *   into the user's message `content`, never replayed on future turns.
 * - Parent (ClauseActionsView) drives canned prompts via a ref-style handler
 *   registration so the Explain dropdown stays in its existing location.
 */

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// useState used for isSending; inputValue is managed by the parent (ClauseActionsView)
import { ClauseContext, IssueChatMessage, useDeskStore } from '../../stores/deskStore';
import { apiService } from '../../services/api';
import { useScopedChatSession } from '../../queries/useScopedChatSession';
import { queryKeys } from '../../queries/queryKeys';
import { escapeForContextInjection } from '../../utils/attachedClauseFormat';
import MarkdownWithCitations from '../chat/MarkdownWithCitations';
import { useChatCitationClick } from '../../hooks/useChatCitationClick';

export interface ClauseChatThreadOptions {
  /** Plan 172: extra bracketed directive merged into context_injection for
   *  THIS turn only. Used by quick-action buttons (Explain ▾) so the verbose
   *  template reaches the LLM without leaking into the persisted user message
   *  content. See Plan 143 for the underlying mechanism. */
  extraContextInjection?: string;
}

export interface ClauseChatThreadHandle {
  sendMessage: (prompt: string, options?: ClauseChatThreadOptions) => void;
}

interface ClauseChatThreadProps {
  clauseContext: ClauseContext;
  /** Plan 171: when non-empty, buildContextInjection widens to include these
   *  contexts after the focal clause, in the order provided. Caller is
   *  responsible for ordering (document order for section selections). The
   *  session and thread storage stay keyed to the focal clauseId; only the
   *  per-turn bracketed context expands. */
  additionalClauseContexts?: ClauseContext[];
}

const EMPTY_THREAD: IssueChatMessage[] = [];

let messageCounter = 0;
const nextMessageId = (): string => `cct-${Date.now()}-${++messageCounter}`;

export const ClauseChatThread = forwardRef<ClauseChatThreadHandle, ClauseChatThreadProps>(({
  clauseContext,
  additionalClauseContexts,
}, ref) => {
  const selectedMatterId = useDeskStore((s) => s.selectedMatterId);
  const { sessionId, isLoading: sessionLoading } = useScopedChatSession(
    selectedMatterId,
    'clause',
    clauseContext.clauseId,
    `Clause: ${clauseContext.clauseTitle}`,
  );

  const thread = useDeskStore((s) => s.clauseChatThreads[clauseContext.clauseId] ?? EMPTY_THREAD);
  const addMessage = useDeskStore((s) => s.addClauseChatMessage);
  const setThread = useDeskStore((s) => s.setClauseChatThread);
  const handleCitationClick = useChatCitationClick();

  const [isSending, setIsSending] = useState(false);
  // Issue #769: synchronous reentry guard. `isSending` (useState) flips one
  // render-tick after `setIsSending(true)`, so two events on the same React
  // closure both passed the `!isSending` gate and both POSTed. A ref flips
  // synchronously. See RightPanelChat.tsx for the full root-cause notes.
  const sendInFlightRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  // Hydrate once per sessionId. The check below re-reads store state at
  // resolve time so concurrent optimistic messages are not overwritten by
  // a late hydration response.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread.length]);

  // Issue #844: deliberately NO hydrated-once ref guard. A ref set synchronously
  // before the async fetch is StrictMode-hostile — dev double-invokes the effect
  // (setup → cleanup → setup); the cleanup cancels the first fetch and the ref
  // guard then short-circuits the second setup, so on a warm-session remount the
  // hydration is skipped and prior messages never load. The `cancelled` flag and
  // the `current.length > 0` race guard keep running per sessionId safe.
  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    apiService
      .getChatMessages(sessionId)
      .then((res: any) => {
        if (cancelled) return;
        const current =
          useDeskStore.getState().clauseChatThreads[clauseContext.clauseId] || [];
        // Race guard: user may have posted in the interim. Do NOT overwrite.
        if (current.length > 0) return;

        const rows: any[] = res?.messages || [];
        if (rows.length === 0) return;
        const messages: IssueChatMessage[] = rows.map((m) => ({
          id: nextMessageId(),
          role: m.role,
          content: m.content,
          timestamp: m.created_at,
          // Issue #761: preserve citations so hydrated assistant turns hover
          // and click identically to fresh turns.
          citations: m.citations || [],
        }));
        setThread(clauseContext.clauseId, messages);
      })
      .catch((err) => {
        console.warn('[ClauseChatThread] hydrate failed', err);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, clauseContext.clauseId, setThread]);

  const buildContextInjection = useCallback((): string => {
    // Plan 171: focal first; when additionalClauseContexts is non-empty,
    // emit one bracketed block per clause separated by \n\n (mirrors the
    // attachedClauseFormat multi-clause layout). Escape title/text the same
    // way to keep the bracketed grammar parseable.
    const renderBlock = (c: ClauseContext) =>
      `[Clause: "${escapeForContextInjection(c.clauseTitle)}"]\n` +
      `[Text: "${escapeForContextInjection(c.clauseText)}"]`;
    if (!additionalClauseContexts || additionalClauseContexts.length === 0) {
      return renderBlock(clauseContext);
    }
    return [clauseContext, ...additionalClauseContexts].map(renderBlock).join('\n\n');
  }, [
    clauseContext.clauseTitle,
    clauseContext.clauseText,
    additionalClauseContexts,
  ]);

  const sendMessage = useCallback(
    async (prompt: string, options?: ClauseChatThreadOptions) => {
      // Issue #769: ref-based reentry guard (synchronous); see field decl.
      if (!sessionId || sendInFlightRef.current) return;
      const trimmed = prompt.trim();
      if (!trimmed) return;

      sendInFlightRef.current = true;
      setIsSending(true);

      const userMsg: IssueChatMessage = {
        id: nextMessageId(),
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      addMessage(clauseContext.clauseId, userMsg);

      const loadingId = nextMessageId();
      addMessage(clauseContext.clauseId, {
        id: loadingId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isLoading: true,
      });

      try {
        const baseInjection = buildContextInjection();
        const mergedInjection = options?.extraContextInjection
          ? `${options.extraContextInjection}\n${baseInjection}`
          : baseInjection;
        const response = await apiService.sendChatMessage(sessionId, trimmed, {
          contextInjection: mergedInjection,
        });
        const content =
          response.assistant_message?.content ||
          response.response ||
          response.content ||
          'No response received.';

        const citations = response.assistant_message?.citations || [];
        useDeskStore.setState((state) => ({
          clauseChatThreads: {
            ...state.clauseChatThreads,
            [clauseContext.clauseId]: (
              state.clauseChatThreads[clauseContext.clauseId] || []
            ).map((m) =>
              m.id === loadingId
                ? { ...m, content, citations, isLoading: false }
                : m,
            ),
          },
        }));
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to get a response. Please try again.';
        useDeskStore.setState((state) => ({
          clauseChatThreads: {
            ...state.clauseChatThreads,
            [clauseContext.clauseId]: (
              state.clauseChatThreads[clauseContext.clauseId] || []
            ).map((m) =>
              m.id === loadingId ? { ...m, content: detail, isLoading: false } : m,
            ),
          },
        }));
      } finally {
        // Issue #769: release the ref alongside the state.
        sendInFlightRef.current = false;
        setIsSending(false);
        // Plan 174 (#763): refresh the badge counts on the document viewer.
        // Runs even on failure because the backend persists the user row
        // before the LLM call (docs/chat-workflow.md:168), so a failed
        // generation still changes the DB count.
        if (selectedMatterId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.matters.scopedChatCounts(selectedMatterId),
          });
        }
      }
    },
    [sessionId, isSending, addMessage, buildContextInjection, clauseContext.clauseId, selectedMatterId, queryClient],
  );

  useImperativeHandle(ref, () => ({ sendMessage }), [sendMessage]);

  return (
    <>
      {/* Messages render inline inside the parent's scrollable body */}
      {thread.map((msg) => (
        <div
          key={msg.id}
          className={`issue-chat-thread__msg issue-chat-thread__msg--${msg.role}`}
        >
          {msg.isLoading ? (
            <div className="issue-chat-thread__loading">
              <span className="typing-indicator">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </span>
              <span className="issue-chat-thread__loading-text">Glyphe is thinking...</span>
            </div>
          ) : (
            <div className={msg.role === 'assistant' ? 'desk-markdown' : ''}>
              {msg.role === 'assistant' ? (
                <MarkdownWithCitations
                  content={msg.content}
                  citations={msg.citations || []}
                  onCitationClick={handleCitationClick}
                />
              ) : (
                msg.content
              )}
            </div>
          )}
        </div>
      ))}
      <div ref={messagesEndRef} />

      {/* Chat input renders in the parent layout's bottom slot via a portal-style
          approach — but since we can't portal from forwardRef easily, the parent
          (ClauseActionsView) renders the input bar itself using the imperative
          handle. We expose inputValue/setInputValue/handleSubmit/handleKeyDown
          as part of the imperative handle for the parent to render. */}
    </>
  );
});

ClauseChatThread.displayName = 'ClauseChatThread';
export default ClauseChatThread;
